import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getCampaignMetrics, getCampaignRecommendations, applyRecommendation } from '../api/campaigns'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ErrorMessage } from '../components/ErrorMessage'
import { useAuth } from '../context/AuthContext'
import type { CampaignMetrics, Recommendation } from '../types'
import styles from './CampaignDetailPage.module.css'

// ─── Inline SVG Line Chart ────────────────────────────────────────────────────

interface LineChartProps {
  data: { date: string; impressions: number; clicks: number; spend: number }[]
}

function PerfChart({ data }: LineChartProps) {
  if (!data.length) return null

  const W = 600, H = 180, padL = 40, padR = 16, padT = 16, padB = 32
  const innerW = W - padL - padR
  const innerH = H - padT - padB

  const maxImp = Math.max(...data.map(d => d.impressions), 1)
  const maxClicks = Math.max(...data.map(d => d.clicks), 1)

  const xScale = (i: number) => padL + (i / (data.length - 1 || 1)) * innerW
  const yScaleImp = (v: number) => padT + innerH - (v / maxImp) * innerH
  const yScaleClicks = (v: number) => padT + innerH - (v / maxClicks) * innerH

  const line = (pts: [number, number][]) =>
    pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')

  const impPts: [number, number][] = data.map((d, i) => [xScale(i), yScaleImp(d.impressions)])
  const clickPts: [number, number][] = data.map((d, i) => [xScale(i), yScaleClicks(d.clicks)])

  const gridLines = [0, 0.25, 0.5, 0.75, 1]

  return (
    <div className={styles.chartWrap}>
      <svg viewBox={`0 0 ${W} ${H}`} className={styles.chartSvg}>
        {gridLines.map(p => {
          const y = padT + innerH * (1 - p)
          return (
            <g key={p}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e5e9f0" strokeWidth="1" />
              <text x={padL - 6} y={y + 4} className={styles.chartAxisLabel} textAnchor="end">
                {Math.round(maxImp * p).toLocaleString()}
              </text>
            </g>
          )
        })}
        {data.map((d, i) => (
          <text key={d.date} x={xScale(i)} y={H - 6} className={styles.chartAxisLabel} textAnchor="middle">
            {d.date.slice(5)}
          </text>
        ))}
        <path d={line(impPts)} className={styles.chartLine} stroke="#1d4ed8" strokeWidth="2.5" />
        <path d={line(clickPts)} className={styles.chartLine} stroke="#6366f1" strokeWidth="2.5" strokeDasharray="5,3" />
        {impPts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3.5" fill="#1d4ed8" className={styles.chartDot} />
        ))}
      </svg>
      <div className={styles.chartLegend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: '#1d4ed8' }} /> Impressions
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: '#6366f1' }} /> Clicks
        </span>
      </div>
    </div>
  )
}

// ─── Recommendation Card ──────────────────────────────────────────────────────

function RecCard({ rec, canApply, onApply, applying }: {
  rec: Recommendation; canApply: boolean; onApply: (id: string) => void; applying: boolean
}) {
  const confidence = Math.round(rec.confidence_score * 100)
  return (
    <div className={`${styles.recCard} ${rec.applied ? styles.recApplied : ''}`}>
      <div className={styles.recHeader}>
        <span className={styles.recGoal}>{rec.goal}</span>
        <span className={`${styles.recConfidence} ${rec.confidence_score < 0.6 ? styles.lowConf : ''}`}>
          {confidence}% confidence
        </span>
        {rec.applied && <span className={styles.appliedBadge}>✓ Applied</span>}
      </div>
      <p className={styles.recAction}>{rec.action}</p>
      <div className={styles.recValues}>
        <div className={styles.recValue}>
          <span className={styles.recValueLabel}>Current</span>
          <span className={styles.recValueNum}>{rec.current_value.toFixed(3)}</span>
        </div>
        <span className={styles.recArrow}>→</span>
        <div className={styles.recValue}>
          <span className={styles.recValueLabel}>Suggested</span>
          <span className={styles.recValueNum}>{rec.suggested_value.toFixed(3)}</span>
        </div>
      </div>
      <p className={styles.recReasoning}>{rec.reasoning}</p>
      <div className={styles.recFooter}>
        <span className={styles.recDate}>{new Date(rec.generated_at).toLocaleDateString()}</span>
        {canApply && !rec.applied && (
          <button className={styles.applyBtn} onClick={() => onApply(rec.recommendation_id)} disabled={applying}>
            {applying ? 'Applying…' : 'Apply'}
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { canWrite } = useAuth()
  const [metrics, setMetrics] = useState<CampaignMetrics[]>([])
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [applyingId, setApplyingId] = useState<string | null>(null)
  const [applyMsg, setApplyMsg] = useState<string | null>(null)

  const load = async () => {
    if (!id) return
    setLoading(true); setError(null)
    try {
      const [metricsData, recsData] = await Promise.all([
        getCampaignMetrics(id),
        getCampaignRecommendations(id),
      ])
      setMetrics(metricsData)
      setRecommendations(recsData)
    } catch { setError('Failed to load campaign data.') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [id])

  const handleApply = async (recId: string) => {
    if (!id) return
    setApplyingId(recId); setApplyMsg(null)
    try {
      await applyRecommendation(id, recId)
      setRecommendations(prev => prev.map(r => r.recommendation_id === recId ? { ...r, applied: true } : r))
      setApplyMsg('Recommendation applied successfully.')
    } catch { setApplyMsg('Failed to apply recommendation.') }
    finally { setApplyingId(null) }
  }

  if (loading) return <LoadingSpinner message="Loading campaign details…" />
  if (error) return <ErrorMessage message={error} onRetry={load} />

  const campaignName = metrics[0]?.campaign_name?.trim() || `Campaign ${id?.slice(-6).toUpperCase()}`
  const latest = metrics[metrics.length - 1]
  const chartData = [...metrics].sort((a, b) => a.date.localeCompare(b.date))

  const kpis = latest ? [
    { label: 'Impressions', value: latest.impressions.toLocaleString() },
    { label: 'Clicks', value: latest.clicks.toLocaleString() },
    { label: 'CTR', value: (latest.ctr * 100).toFixed(2) + '%' },
    { label: 'Spend', value: '₹' + latest.spend.toFixed(2) },
    { label: 'CPC', value: '₹' + latest.cpc.toFixed(2) },
    { label: 'ROAS', value: latest.roas.toFixed(2) + 'x' },
    { label: 'Reach', value: latest.reach.toLocaleString() },
    { label: 'Frequency', value: latest.frequency.toFixed(2) },
  ] : []

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <Link to="/dashboard/campaigns" className={styles.breadcrumbLink}>Campaigns</Link>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>{campaignName}</span>
      </nav>

      <h1 className={styles.heading}>{campaignName}</h1>

      {/* KPI Strip */}
      {kpis.length > 0 && (
        <div className={styles.kpiStrip}>
          {kpis.map(k => (
            <div key={k.label} className={styles.kpiCard}>
              <span className={styles.kpiLabel}>{k.label}</span>
              <span className={styles.kpiValue}>{k.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Performance Chart */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Performance Over Time</h2>
        </div>
        <div className={styles.sectionBody}>
          {chartData.length === 0
            ? <p className={styles.empty}>No metrics data available.</p>
            : <PerfChart data={chartData} />
          }
        </div>
      </div>

      {/* Recommendations */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>AI Recommendations</h2>
          <span style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
            {recommendations.length} suggestions
          </span>
        </div>
        <div className={styles.sectionBody}>
          {applyMsg && <div className={styles.applyMsg}>{applyMsg}</div>}
          {recommendations.length === 0
            ? <p className={styles.empty}>No recommendations available.</p>
            : (
              <div className={styles.recsGrid}>
                {recommendations.map(rec => (
                  <RecCard
                    key={rec.recommendation_id}
                    rec={rec}
                    canApply={canWrite}
                    onApply={handleApply}
                    applying={applyingId === rec.recommendation_id}
                  />
                ))}
              </div>
            )
          }
        </div>
      </div>
    </div>
  )
}
